/**
 * Utility functions for enhanced search functionality
 */

/**
 * Removes diacritics (accents) from a string for accent-insensitive comparison
 */
function removeDiacritics(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * Splits a search term into individual words and creates a more flexible search
 * that matches regardless of word order and ignores accents/diacritics
 */
export function createUniversalSearchMatcher(searchTerm: string) {
  if (!searchTerm.trim()) {
    return () => true;
  }

  const searchWords = removeDiacritics(searchTerm)
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .filter(word => word.length > 0);

  return (text: string): boolean => {
    if (!text) return false;

    const normalizedText = removeDiacritics(text).toLowerCase();

    // All search words must be found somewhere in the text
    return searchWords.every(word => normalizedText.includes(word));
  };
}

/**
 * Enhanced candidate search that checks multiple fields with universal matching
 */
export function matchesCandidateSearch(
  candidate: {
    name: string;
    email?: string;
    function?: string;
    skills?: string[];
  },
  searchTerm: string
): boolean {
  if (!searchTerm.trim()) {
    return true;
  }

  // Combine all candidate data into one searchable text
  const searchableFields = [
    candidate.name,
    candidate.email || '',
    candidate.function || '',
    ...(candidate.skills || [])
  ];

  const combinedText = searchableFields
    .filter(field => field && field.trim().length > 0)
    .join(' ');

  // Use universal matcher on the combined text
  const matcher = createUniversalSearchMatcher(searchTerm);
  return matcher(combinedText);
}

/**
 * Enhanced company search that checks multiple fields with universal matching
 */
export function matchesCompanySearch(
  company: {
    name: string;
    street?: string | null;
    city?: string | null;
    country?: string | null;
    assignee?: string | null;
    vat_number?: string | null;
    website_url?: string | null;
    notes?: string | null;
  },
  searchTerm: string
): boolean {
  if (!searchTerm.trim()) {
    return true;
  }

  // Combine all company data into one searchable text
  const searchableFields = [
    company.name,
    company.street || '',
    company.city || '',
    company.country || '',
    company.assignee || '',
    company.vat_number || '',
    company.website_url || ''
  ];

  const combinedText = searchableFields
    .filter(field => field && field.trim().length > 0)
    .join(' ');

  // Use universal matcher on the combined text
  const matcher = createUniversalSearchMatcher(searchTerm);
  return matcher(combinedText);
}

/**
 * Enhanced job search that checks multiple fields with universal matching
 */
export function matchesJobSearch(
  job: {
    title: string;
    description?: string | null;
    skills?: string[];
    hardSkills?: string[];
    softSkills?: string[];
    employmentType?: string | null;
    timeCommitment?: string | null;
    seniorityLevel?: string | null;
    company?: { name: string } | null;
  },
  searchTerm: string
): boolean {
  if (!searchTerm.trim()) {
    return true;
  }

  // Combine all job data into one searchable text
  const searchableFields = [
    job.title,
    job.description || '',
    job.company?.name || '',
    job.employmentType || '',
    job.timeCommitment || '',
    job.seniorityLevel || '',
    ...(job.skills || []),
    ...(job.hardSkills || []),
    ...(job.softSkills || [])
  ];

  const combinedText = searchableFields
    .filter(field => field && field.trim().length > 0)
    .join(' ');

  // Use universal matcher on the combined text
  const matcher = createUniversalSearchMatcher(searchTerm);
  return matcher(combinedText);
}

/**
 * Enhanced list search that checks multiple fields with universal matching
 */
export function matchesListSearch(
  list: {
    name: string;
    description?: string | null;
    created_by?: string;
    custom_fields?: Record<string, any>;
  },
  searchTerm: string
): boolean {
  if (!searchTerm.trim()) {
    return true;
  }

  // Combine all list data into one searchable text
  const searchableFields = [
    list.name,
    list.description || '',
    list.created_by || '',
    // Add custom fields values if they exist
    ...(list.custom_fields ? Object.values(list.custom_fields).filter(val => 
      typeof val === 'string' && val.trim().length > 0
    ) : [])
  ];

  const combinedText = searchableFields
    .filter(field => field && field.trim().length > 0)
    .join(' ');

  // Use universal matcher on the combined text
  const matcher = createUniversalSearchMatcher(searchTerm);
  return matcher(combinedText);
}

/**
 * Enhanced list member search that checks candidate fields with universal matching
 */
export function matchesListMemberSearch(
  member: {
    candidate?: {
      name: string;
      email?: string;
      function?: string;
      skills?: string[];
    };
  },
  searchTerm: string
): boolean {
  if (!searchTerm.trim() || !member.candidate) {
    return !searchTerm.trim(); // Return true only if no search term
  }

  // Use the existing candidate search function
  return matchesCandidateSearch(member.candidate, searchTerm);
}

/**
 * Enhanced event search that checks multiple fields with universal matching
 */
export function matchesEventSearch(
  event: {
    title: string;
    description?: string | null;
    location?: string | null;
    status?: string | null;
    priority?: string | null;
  },
  searchTerm: string
): boolean {
  if (!searchTerm.trim()) {
    return true;
  }

  const searchableFields = [
    event.title,
    event.description || '',
    event.location || '',
    event.status || '',
    event.priority || ''
  ];

  const combinedText = searchableFields
    .filter(field => field && field.trim().length > 0)
    .join(' ');

  const matcher = createUniversalSearchMatcher(searchTerm);
  return matcher(combinedText);
}

/**
 * Enhanced task search that checks multiple fields with universal matching
 */
export function matchesTaskSearch(
  task: {
    name: string;
    description?: string | null;
    status?: string | null;
    priority?: string | null;
  },
  searchTerm: string
): boolean {
  if (!searchTerm.trim()) {
    return true;
  }

  // Combine all task data into one searchable text
  const searchableFields = [
    task.name,
    task.description || '',
    task.status || '',
    task.priority || ''
  ];

  const combinedText = searchableFields
    .filter(field => field && field.trim().length > 0)
    .join(' ');

  // Use universal matcher on the combined text
  const matcher = createUniversalSearchMatcher(searchTerm);
  return matcher(combinedText);
}
